// Very small safe expression parser supporting identifiers (with dot access),
// numbers, booleans, string literals ('...' or "..."),
// comparison (==, !=, >=, <=, >, <), logical (&&, ||), and parentheses.
export class ConditionParser {
  constructor() {}

  evaluate(payload: Record<string, unknown>, expression: string): boolean {
    const tokens = this.tokenize(expression);
    let index = 0;

    const parsePrimary = (): any => {
      const tok = tokens[index++];
      if (tok === '(') {
        const v = parseOr();
        if (tokens[index++] !== ')') throw new Error('Missing )');
        return v;
      }
      // string literal
      if (
        typeof tok === 'string' &&
        tok.length >= 2 &&
        ((tok.startsWith("'") && tok.endsWith("'")) || (tok.startsWith('"') && tok.endsWith('"')))
      ) {
        return this.unescapeString(tok.slice(1, -1));
      }
      if (tok === 'true' || tok === 'false') return tok === 'true';
      if (tok && /^[0-9]+(\.[0-9]+)?$/.test(tok)) return Number(tok);
      if (tok && /^[a-zA-Z_][a-zA-Z0-9_\.]*$/.test(tok)) {
        // identifier: may contain dots (nested keys)
        const parts = tok.split('.');
        let value: any = payload;
        for (const key of parts) {
          if (value == null) {
            value = undefined;
            break;
          }
          value = (value as any)[key];
        }
        return value;
      }
      throw new Error('Unexpected token: ' + tok);
    };

    const parseComparison = (): any => {
      let left = parsePrimary();
      while (index < tokens.length) {
        const op = tokens[index];
        if (!['==','!=','>=','<=','>','<'].includes(op)) break;
        index++;
        const right = parsePrimary();
        switch (op) {
          case '==': left = left == right; break;
          case '!=': left = left != right; break;
          case '>=': left = (left as any) >= (right as any); break;
          case '<=': left = (left as any) <= (right as any); break;
          case '>': left = (left as any) > (right as any); break;
          case '<': left = (left as any) < (right as any); break;
        }
      }
      return left;
    };

    const parseAnd = (): any => {
      let left = parseComparison();
      while (tokens[index] === '&&') {
        index++;
        const right = parseComparison();
        left = Boolean(left) && Boolean(right);
      }
      return left;
    };

    const parseOr = (): any => {
      let left = parseAnd();
      while (tokens[index] === '||') {
        index++;
        const right = parseAnd();
        left = Boolean(left) || Boolean(right);
      }
      return left;
    };

    const result = parseOr();
    if (index !== tokens.length) throw new Error('Unexpected tokens at end');
    return Boolean(result);
  }

  private tokenize(expr: string): string[] {
    const tokens: string[] = [];
    // groups: 1=paren, 2=op, 3=dquoted content, 4=squoted content, 5=ident, 6=num, 7=bool
    const re = /\s*([()])|\s*(>=|<=|==|!=|&&|\|\||>|<)|\s*"((?:[^"\\]|\\.)*)"|\s*'((?:[^'\\]|\\.)*)'|\s*([a-zA-Z_][a-zA-Z0-9_\.]*)|\s*([0-9]+(?:\.[0-9]+)?)|\s*(true|false)/g;
    let m: RegExpExecArray | null;
    let lastIndex = 0;
    while ((m = re.exec(expr)) !== null) {
      if (m.index !== lastIndex) {
        const skipped = expr.slice(lastIndex, m.index).trim();
        if (skipped.length) throw new Error('Invalid token near: ' + skipped);
      }
      lastIndex = re.lastIndex;
      const [, paren, op, dquoted, squoted, ident, num, bool] = m as unknown as [
        any,
        string,
        string,
        string,
        string,
        string,
        string,
        string
      ];
      if (paren) tokens.push(paren);
      else if (op) tokens.push(op);
      else if (dquoted !== undefined) tokens.push('"' + dquoted + '"');
      else if (squoted !== undefined) tokens.push("'" + squoted + "'");
      else if (ident) tokens.push(ident);
      else if (num) tokens.push(num);
      else if (bool) tokens.push(bool);
    }
    if (lastIndex !== expr.length) {
      const leftover = expr.slice(lastIndex).trim();
      if (leftover.length) throw new Error('Invalid trailing token: ' + leftover);
    }
    return tokens;
  }

  private unescapeString(raw: string): string {
    return raw.replace(/\\([\\'"nrt])/g, (_match, ch: string) => {
      switch (ch) {
        case 'n':
          return '\n';
        case 'r':
          return '\r';
        case 't':
          return '\t';
        case '\\':
          return '\\';
        case '"':
          return '"';
        case "'":
          return "'";
        default:
          return ch;
      }
    });
  }
}


